/**
 * App.tsx — Application Root & Route Guards
 * 
 * =============================================================================
 * INVARIANTS (see docs/invariants/route-guard-invariants.md)
 * =============================================================================
 * 
 * 6.1 NO PARTIAL ENTRY
 *     A route may render only if:
 *     - Auth is resolved
 *     - Profile is resolved
 *     - Onboarding state is unambiguous
 *     Otherwise: show loading, recovery, or redirect
 * 
 * 6.2 GUARDS ARE DETERMINISTIC
 *     Given the same auth + profile state:
 *     - Routing outcome must be identical
 *     - No hidden side effects
 *     - No async mutation inside guards
 * 
 * =============================================================================
 */
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, lazy, Suspense, useRef } from "react";
import { supabase } from "./integrations/supabase/client";

// Lazy load pages for better performance
const Timer = lazy(() => import("./pages/Timer"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Profile = lazy(() => import("./pages/Profile"));
const Shop = lazy(() => import("./pages/Shop"));
const Groups = lazy(() => import("./pages/Groups"));
const Admin = lazy(() => import("./pages/Admin"));
const BreakTimer = lazy(() => import("./pages/BreakTimer"));
const BreakComplete = lazy(() => import("./pages/BreakComplete"));

// Keep auth-related pages non-lazy for faster initial load
import Onboarding from "./pages/Onboarding";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import DataDeletion from "./pages/DataDeletion";
import ResetPassword from "./pages/ResetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import Landing from "./pages/Landing";
import Features from "./pages/Features";
import Pricing from "./pages/Pricing";
import About from "./pages/About";
import { Navigation } from "./components/Navigation";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { UserProvider, useUser } from "@/contexts/user";
import { FeatureFlagsProvider } from "@/contexts/FeatureFlagsContext";
import { DeveloperModeProvider } from "@/contexts/DeveloperModeContext";
import { TooltipAnchorProvider } from "@/contexts/TooltipAnchorContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthErrorBoundary } from "./components/AuthErrorBoundary";
import { NetworkBanner } from "./components/NetworkBanner";
import { DeveloperModeToggle } from "./components/DeveloperModeToggle";
import { useToast } from "@/hooks/use-toast";
import { debug } from "@/utils/debug";

// Import unified loading skeleton
import { UnifiedLoadingSkeleton } from "./components/UnifiedLoadingSkeleton";
import { LoadingRecoveryOverlay } from "./components/LoadingRecoveryOverlay";
import { TooltipGuideManager } from "./components/TooltipGuideManager";

const queryClient = new QueryClient();

// Shared handle validation logic
const isValidHandle = (handle: string | null | undefined): boolean => {
  return !!(handle && 
            !handle.startsWith('temp_') && 
            handle.length >= 3);
};

// Check user profile with timeout to handle slow connections
const checkUserProfileWithTimeout = async (userId: string, timeoutMs = 5000): Promise<{
  hasValidHandle: boolean;
  error?: string;
}> => {
  try {
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Profile check timeout')), timeoutMs)
    );
    
    const queryPromise = supabase
      .from('user_profiles')
      .select('handle')
      .eq('id', userId)
      .single();
    
    const { data, error } = await Promise.race([
      queryPromise,
      timeoutPromise
    ]) as any;
    
    if (error) {
      debug.error('[checkUserProfile] Error:', error);
      return { hasValidHandle: false, error: error.message };
    }
    
    return { hasValidHandle: isValidHandle(data?.handle) };
  } catch (err: any) {
    debug.error('[checkUserProfile] Exception:', err);
    return { hasValidHandle: false, error: err.message };
  }
};

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const { user: userProfile } = useUser();
  const { toast } = useToast();

  useEffect(() => {
    if (!loading && user && userProfile) {
      if (!userProfile.isAdmin) {
        toast({
          title: "Access Denied",
          description: "You must be an admin to access this page.",
          variant: "destructive"
        });
        // V2: Use navigate instead of window.location.replace
        // Per theme-architecture.md: Full reloads cause theme bootstrap re-run → flicker
      }
    }
  }, [loading, user, userProfile, toast]);

  if (loading || !userProfile) {
    return <UnifiedLoadingSkeleton context="admin" />;
  }

  if (!userProfile.isAdmin) {
    return null;
  }

  return <>{children}</>;
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, session, loading, sessionExpired, onboardingCompleted, signOut } = useAuth();
  const { user: profileUser, loading: profileLoading, profileTimedOut, retryProfileLoad } = useUser();
  const { toast } = useToast();
  const navigate = useNavigate();

  // V3: Handler to delete corrupted account
  const handleDeleteAccount = async () => {
    try {
      const { error } = await supabase.functions.invoke('delete-user-account');
      if (error) throw error;
      toast({
        title: "Account Deleted",
        description: "Your account has been deleted. Redirecting to sign in...",
      });
      await signOut();
    } catch (error: any) {
      toast({
        title: "Deletion Failed",
        description: "Unable to delete account. Please try signing out and contacting support.",
        variant: "destructive"
      });
    }
  };

  // V3: Check for timeout BEFORE loading check - show recovery UI
  if (profileTimedOut) {
    return (
      <LoadingRecoveryOverlay 
        onRetry={retryProfileLoad}
        onSignOut={signOut}
        onDeleteAccount={handleDeleteAccount}
      />
    );
  }

  // 1. AuthContext still loading initial session check
  if (loading) {
    return <UnifiedLoadingSkeleton context="auth" message="Verifying session..." />;
  }

  // 2. Session expired - redirect with warning
  if (sessionExpired) {
    return <Navigate to="/auth" replace />;
  }

  // 3. Not authenticated - redirect BEFORE checking profile (prevents infinite loading)
  if (!user || !session) {
    return <Navigate to="/auth" replace />;
  }

  // 4. User IS authenticated - now check if profile has loaded
  const profileNotYetLoaded = profileUser?.id === 'user-default-id';
  if (profileLoading || profileNotYetLoaded) {
    return <UnifiedLoadingSkeleton context="auth" message="Loading profile..." />;
  }

  // ERROR CASE: onboardingCompleted should never be null after loading completes
  // This indicates a corrupted state - force re-auth
  if (onboardingCompleted === null) {
    toast({
      title: "Session Error",
      description: "Please sign in again.",
      variant: "destructive"
    });
    signOut();
    return <Navigate to="/auth" replace />;
  }

  // Onboarding not completed - redirect to onboarding
  if (onboardingCompleted === false) {
    return <Navigate to="/onboarding" replace />;
  }

  // Check for incomplete profile (missing handle) - show recovery UI on ALL protected pages
  // Only check after profile has loaded (profileNotYetLoaded is false at this point)
  const hasIncompleteProfile = !profileUser?.handle || 
    profileUser.handle === 'unknown_user';

  if (hasIncompleteProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center space-y-6">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl font-bold mb-2">Profile Setup Incomplete</h2>
            <p className="text-muted-foreground">
              Your profile is missing required information. Please complete your profile setup.
            </p>
          </div>
          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/onboarding')}
              className="w-full"
            >
              Complete Profile Setup
            </Button>
            <Button 
              onClick={() => signOut()}
              variant="outline"
              className="w-full"
            >
              Sign Out
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // All good - render protected content
  return <>{children}</>;
};

const OnboardingRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, session, loading, onboardingCompleted, signOut } = useAuth();
  const { user: profileUser, loading: profileLoading, profileTimedOut, retryProfileLoad } = useUser();
  const { toast } = useToast();

  // V4: Check for timeout BEFORE loading check - show recovery UI
  if (profileTimedOut) {
    return (
      <LoadingRecoveryOverlay 
        onRetry={retryProfileLoad}
        onSignOut={signOut}
      />
    );
  }

  // V4 FIX: Check for "not authenticated" BEFORE profile loading gate
  // This prevents infinite skeleton when user has no session (e.g., email not confirmed)
  if (!loading && (!user || !session)) {
    debug.warn('[OnboardingRoute] No session - redirecting to /auth');
    return <Navigate to="/auth" replace />;
  }

  // Still loading auth state
  if (loading) {
    return <UnifiedLoadingSkeleton context="auth" message="Verifying session..." />;
  }

  // Profile still has default ID - means UserContext hasn't loaded yet
  // V4: Only check profile loading if user IS authenticated
  const profileNotYetLoaded = profileUser?.id === 'user-default-id';
  if (profileLoading || profileNotYetLoaded) {
    return <UnifiedLoadingSkeleton context="auth" message="Setting up your account..." />;
  }

  // ERROR CASE: null means corrupted state
  if (onboardingCompleted === null) {
    toast({
      title: "Session Error", 
      description: "Please sign in again.",
      variant: "destructive"
    });
    signOut();
    return <Navigate to="/auth" replace />;
  }

  // Allow access to onboarding if profile is incomplete (missing handle)
  // even if onboardingCompleted flag is true - this handles edge cases
  const hasIncompleteProfile = !profileUser?.handle || 
    profileUser.handle === 'unknown_user';

  // Already completed onboarding AND has complete profile - redirect to main app
  if (onboardingCompleted === true && !hasIncompleteProfile) {
    return <Navigate to="/timer" replace />;
  }

  // Render onboarding flow
  return <>{children}</>;
};

const RequireBreakState = ({ 
  children, 
  requireSession = true 
}: { 
  children: React.ReactNode;
  requireSession?: boolean;
}) => {
  const location = useLocation();
  const state = location.state as any;
  
  const hasValidState = () => {
    // First check location.state (normal navigation)
    if (requireSession) {
      if (state?.sessionId && state?.recommendedBreak) {
        return true;
      }
    } else {
      if (state?.coinsEarned !== undefined && state?.breakType) {
        return true;
      }
    }
    
    // For /rest route: also check sessionStorage for saved break session
    if (requireSession) {
      try {
        const saved = sessionStorage.getItem('activeBreakSession');
        if (saved) {
          const session = JSON.parse(saved);
          // Validate essential fields exist
          if (session.startTime && session.totalDuration && session.sessionId !== undefined) {
            // Check session isn't expired (totalDuration + 5 min buffer)
            const maxAge = (session.totalDuration + 300) * 1000;
            if (Date.now() - session.startTime < maxAge) {
              return true;
            }
          }
        }
      } catch (e) {
        // Invalid JSON or other error - continue to redirect
      }
    }
    
    return false;
  };
  
  if (!hasValidState()) {
    return <Navigate to="/timer" replace state={{ 
      warning: 'Invalid break state. Please start a session first.' 
    }} />;
  }
  
  return <>{children}</>;
};

const AppRoutes = () => {
  const { user, loading, onboardingCompleted, signOut } = useAuth();
  
  if (loading) {
    return <UnifiedLoadingSkeleton context="auth" />;
  }
  
  return (
    <Suspense fallback={<UnifiedLoadingSkeleton />}>
      <Routes>
        {/* Public Marketing Pages */}
        <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <Landing />} />
        <Route path="/features" element={<Features />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        
        {/* Auth Pages - V2: Deterministic routing based on onboardingCompleted */}
        <Route path="/auth" element={
          loading ? <UnifiedLoadingSkeleton context="auth" /> :
          !user ? <Auth /> :
          onboardingCompleted === false ? <Navigate to="/onboarding" replace /> :
          onboardingCompleted === true ? <Navigate to="/timer" replace /> :
          // null/corrupt state - sign out and show auth
          (() => { signOut(); return <Auth />; })()
        } />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/data-deletion" element={<DataDeletion />} />
        
        {/* Onboarding */}
        <Route path="/onboarding" element={
          <OnboardingRoute>
            <Onboarding />
          </OnboardingRoute>
        } />
        
        {/* Protected App Pages */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Suspense fallback={<UnifiedLoadingSkeleton route="/dashboard" />}>
              <Dashboard />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/timer" element={
          <ProtectedRoute>
            <Suspense fallback={<UnifiedLoadingSkeleton route="/timer" />}>
              <Timer />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/rest" element={
          <ProtectedRoute>
            <RequireBreakState requireSession={true}>
              <Suspense fallback={<UnifiedLoadingSkeleton route="/rest" />}>
                <BreakTimer />
              </Suspense>
            </RequireBreakState>
          </ProtectedRoute>
        } />
        <Route path="/rest/complete" element={
          <ProtectedRoute>
            <RequireBreakState requireSession={false}>
              <Suspense fallback={<UnifiedLoadingSkeleton route="/rest/complete" />}>
                <BreakComplete />
              </Suspense>
            </RequireBreakState>
          </ProtectedRoute>
        } />
        <Route path="/leaderboard" element={
          <ProtectedRoute>
            <Suspense fallback={<UnifiedLoadingSkeleton route="/leaderboard" />}>
              <Leaderboard />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <AdminRoute>
            <Suspense fallback={<UnifiedLoadingSkeleton context="admin" />}>
              <Admin />
            </Suspense>
          </AdminRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Suspense fallback={<UnifiedLoadingSkeleton route="/profile" />}>
              <Profile />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/shop" element={
          <ProtectedRoute>
            <Suspense fallback={<UnifiedLoadingSkeleton route="/shop" />}>
              <Shop />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/groups" element={
          <ProtectedRoute>
            <Suspense fallback={<UnifiedLoadingSkeleton route="/groups" />}>
              <Groups />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const AppContent = () => {
  const { user } = useAuth();
  const { user: userProfile } = useUser();
  
  return (
    <BrowserRouter>
      <NetworkBanner />
      <div className="min-h-screen bg-background">
        <AppRoutes />
        {user && <Navigation />}
        {user && (import.meta.env.DEV || userProfile?.isAdmin) && <DeveloperModeToggle />}
      </div>
      <TooltipGuideManager />
    </BrowserRouter>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <FeatureFlagsProvider>
          <UserProvider>
            <DeveloperModeProvider>
              <TooltipAnchorProvider>
                <AuthErrorBoundary>
                  <TooltipProvider>
                    <Toaster />
                    <Sonner />
                    <AppContent />
                  </TooltipProvider>
                </AuthErrorBoundary>
              </TooltipAnchorProvider>
            </DeveloperModeProvider>
          </UserProvider>
        </FeatureFlagsProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
